"use client";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setIsNavigatingAction } from "../../store/slices/commonSlice";

/** Fallback timeout duration in milliseconds */
const FALLBACK_TIMEOUT_MS = 6000;

/**
 * Custom hook to handle navigation state reset.
 * Resets the navigation state when:
 * - Page is being hidden/unloaded (pagehide event)
 * - Page becomes visible again (visibilitychange event)
 * - Fallback: After 6 seconds if navigation hasn't completed
 *
 * @returns {boolean} Current navigation state
 */
const useNavigationReset = () => {
  const dispatch = useDispatch();
  const isNavigating = useSelector((state) => state?.common?.isNavigating);

  useEffect(() => {
    if (!isNavigating) return;

    /**
     * Handles pagehide event - fires when page is being hidden/unloaded
     * Resets navigation state when page is definitely unloading
     */
    const handlePageHide = () => {
      dispatch(setIsNavigatingAction(false));
    };

    /**
     * Handles visibilitychange event - fires when page visibility changes
     * Resets state when page becomes visible again (navigation might have been cancelled)
     */
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        dispatch(setIsNavigatingAction(false));
      }
    };

    /**
     * Fallback timeout - resets navigation state if redirection
     * doesn't complete within the specified duration
     */
    const fallbackTimeoutId = setTimeout(() => {
      dispatch(setIsNavigatingAction(false));
    }, FALLBACK_TIMEOUT_MS);

    window.addEventListener("pagehide", handlePageHide);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", handlePageHide);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearTimeout(fallbackTimeoutId);
    };
  }, [isNavigating, dispatch]);

  return isNavigating;
};

export default useNavigationReset;
